import React from 'react';
import styled from 'styled-components';

import { SEGMENTED_SELECTED_BG, SEGMENTED_SELECTED_COLOR } from './segmented-control-style';

const ModeGroup = styled.div`
  display: flex;
  margin-top: 8px;
  border: 1px solid
    var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  border-radius: var(--dcm-cms-bg-field-mode-border-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)));
  overflow: hidden;
`;

const ModeOption = styled.label<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  justify-content: center;
  padding: var(--dcm-cms-segmented-padding, 12px 8px);
  background: ${({ $selected }) =>
    $selected
      ? `var(--dcm-cms-bg-field-mode-selected-bg, ${SEGMENTED_SELECTED_BG})`
      : 'var(--dcm-cms-bg-field-mode-bg, var(--puck-field-color-bg, var(--puck-color-surface, #fff)))'};
  color: ${({ $selected }) =>
    $selected
      ? `var(--dcm-cms-bg-field-mode-selected-text-color, ${SEGMENTED_SELECTED_COLOR})`
      : 'var(--dcm-cms-bg-field-mode-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))'};
  cursor: pointer;
  font-size: var(--dcm-cms-segmented-font-size, var(--puck-font-size-xxs, 14px));
  font-weight: var(--dcm-cms-segmented-font-weight, 400);
  text-align: center;

  & + & {
    border-left: 1px solid
      var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  }

  &:focus-within {
    outline: var(--puck-border-width-focus, 2px) solid
      var(--dcm-cms-bg-field-mode-focus-ring-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: -2px;
    z-index: 1;
  }

  &:has(input:disabled) {
    cursor: default;
    color: var(
      --dcm-cms-bg-field-mode-disabled-text-color,
      var(--puck-field-color-text-disabled, var(--puck-color-text-secondary, #646d72))
    );
  }
`;

const ModeInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

type InheritCustomToggleProps = {
  /** Field label, used for the radiogroup's accessible name. */
  label: string;
  /** Radio group name and test id stem: `<testIdPrefix>-<name>-inherit|custom`. */
  name: string;
  testIdPrefix: string;
  isInherit: boolean;
  onSelect: (mode: 'custom' | 'inherit') => void;
  readOnly?: boolean;
};

/** The two-option Inherit / Custom segmented switch shared by the color controls. */
export function InheritCustomToggle({
  label,
  name,
  testIdPrefix,
  isInherit,
  onSelect,
  readOnly,
}: InheritCustomToggleProps): JSX.Element {
  return (
    <ModeGroup role='radiogroup' aria-label={`${label} mode`}>
      <ModeOption $selected={isInherit}>
        <ModeInput
          type='radio'
          name={`${name}-mode`}
          value='inherit'
          checked={isInherit}
          onChange={() => onSelect('inherit')}
          disabled={readOnly}
          data-testid={`${testIdPrefix}-${name}-inherit`}
        />
        Inherit
      </ModeOption>
      <ModeOption $selected={!isInherit}>
        <ModeInput
          type='radio'
          name={`${name}-mode`}
          value='custom'
          checked={!isInherit}
          onChange={() => onSelect('custom')}
          disabled={readOnly}
          data-testid={`${testIdPrefix}-${name}-custom`}
        />
        Custom
      </ModeOption>
    </ModeGroup>
  );
}
